"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Sliders, Settings as SettingsIcon, Users, Building2, User, Grid2x2, ArrowDownUp, Bell, Smile, Waypoints } from "lucide-react";
// Unused imports (commented tabs): ArrowUpRightSquare, Shield, CheckCircle2, Sparkles, Globe, Download, ArrowUpCircle
import { useSettingsModal } from "@/contexts/settingsModalContext";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { cn } from "@/lib/utils";
import PreferencesContent from "../tabs/PreferencesContent";
import GeneralContent from "../tabs/GeneralContent";
import PeopleContent from "../tabs/PeopleContent";
import WorkAreasContent from "../tabs/WorkAreasContent";
import IntegrationsContent from "../tabs/IntegrationsContent";
import ClosotMcpContent from "../tabs/ClosotMcpContent";
import ImportExportContent from "../tabs/ImportExportContent";
import AccountSettingsContent from "../tabs/AccountSettingsContent";
import NotificationsContent from "../tabs/NotificationsContent";
import EmojisContent from "../tabs/EmojisContent";
import DeleteConfirmationModal from "@/components/tailwind/ui/deleteConfirmationModal";

interface SettingsTab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  section?: string;
  highlight?: boolean;
}

const ACCOUNT_TABS: SettingsTab[] = [
  { id: "profile", label: "My account", icon: User, section: "Account" },
];

const USER_TABS: SettingsTab[] = [
  { id: "user_settings", label: "Preferences", icon: Sliders, section: "Account" },
  { id: "notifications", label: "Notifications", icon: Bell, section: "Account" },
  { id: "integrations", label: "Connections", icon: Grid2x2, section: "Account" },
  { id: "closot_mcp", label: "Closot MCP", icon: Waypoints, section: "Account" },

  // { id: "connected_apps", label: "Connections", icon: ArrowUpRightSquare, section: "Account" },
];

const WORKSPACE_TABS: SettingsTab[] = [
  { id: "settings", label: "General", icon: SettingsIcon, section: "Workspace" },
  { id: "members", label: "People", icon: Users, section: "Workspace" },
  { id: "teams", label: "Workareas", icon: Building2, section: "Workspace" },
  { id: "custom_emoji", label: "Emoji", icon: Smile, section: "Workspace" },
  { id: "import_export", label: "Import & Export", icon: ArrowDownUp, section: "Workspace" },
  // { id: "security", label: "Security", icon: Shield, section: "Workspace" },
  // { id: "identity_provisioning", label: "Identity", icon: CheckCircle2, section: "Workspace" },
  // { id: "ai", label: "Closot AI", icon: Sparkles, section: "Workspace" },
  // { id: "public_pages", label: "Public pages", icon: Globe, section: "Workspace" },
  // { id: "integrations", label: "Connections", icon: Grid2x2, section: "Workspace" },
  // { id: "imports", label: "Import", icon: Download, section: "Workspace" },
  // { id: "plans", label: "Upgrade plan", icon: ArrowUpCircle, section: "Workspace", highlight: true },
];

const ALL_TABS = [...ACCOUNT_TABS, ...USER_TABS, ...WORKSPACE_TABS];

function SettingsModalContent() {
  const {
    isOpen,
    closeModal,
    activeTab,
    setActiveTab,
    showDeleteWorkspaceModal,
    setShowDeleteWorkspaceModal,
    isDeletingWorkspace,
    confirmDeleteWorkspace,
  } = useSettingsModal();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaceContext();
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, closeModal]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const element = target as Element;

      // Don't close if click is on MemberActionsMenu, its backdrop, a Radix
      // Popover, any portaled AnchoredPopover panel, the import modal, or the
      // image crop modal
      if (
        element.closest('.member-actions-menu') ||
        element.closest('[data-member-actions-backdrop="true"]') ||
        element.getAttribute('data-member-actions-backdrop') === 'true' ||
        element.closest('[data-radix-popper-content-wrapper]') ||
        element.closest('[data-anchored-popover]') ||
        element.closest('[data-import-modal="true"]') ||
        element.closest('[data-image-crop-modal="true"]') ||
        element.closest('[data-mcp-connect-modal="true"]')
      ) {
        return;
      }

      if (modalRef.current && !modalRef.current.contains(target)) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeModal]);

  if (!isOpen) return null;

  const tabsBySection = ALL_TABS.reduce((acc, tab) => {
    const section = tab.section || "Other";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section]!.push(tab);
    return acc;
  }, {} as Record<string, SettingsTab[]>);

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return <AccountSettingsContent />;
      case "user_settings":
        return <PreferencesContent />;
      case "notifications":
        return <NotificationsContent />;
      case "settings":
        return <GeneralContent />;
      case "members":
        return <PeopleContent />;
      case "teams":
        return <WorkAreasContent />;
      case "custom_emoji":
        return <EmojisContent />;
      case "integrations":
        return <IntegrationsContent />;
      case "closot_mcp":
        return <ClosotMcpContent />;
      case "import_export":
        return <ImportExportContent />;
      default:
        return <PreferencesContent />;
    }
  };

  return createPortal(
    // Bottom-sheet on phones AND tablets-portrait (anything below `lg`,
    // i.e. < 1024px), centered desktop modal at `lg+`. Uses the
    // same breakpoint — tablets benefit from the sheet's full-width feel
    // because their portrait viewport (768–1023px) is too narrow for a
    // comfortable centered modal with sidebar + content.
    <div className="fixed inset-0 z-[9999] flex items-end lg:items-center justify-center bg-black/50 dark:bg-black/70">
      <div
        ref={modalRef}
        // < lg: full width, 90% of dynamic viewport height (room for
        //   backdrop above + plays nice with iOS keyboard), only top corners
        //   rounded.
        // lg+ (laptops/desktops): identical to today — 1150px wide,
        //   ~100vh-100px tall, fully rounded, centered.
        className="relative z-[1] overflow-hidden mb-0 bg-background shadow-xl w-full h-[90dvh] rounded-t-xl lg:w-[1150px] lg:max-w-[calc(100vw-100px)] lg:h-[calc(100vh-100px)] lg:max-h-[715px] lg:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Settings & members"
        tabIndex={-1}
      >
        {/* Outer split: stacked < lg (sidebar on top, content below), row at lg+. */}
        <div className="flex h-full flex-col lg:flex-row border border-component">
          {/* Sidebar — full-width horizontal tab strip below `lg`, 240px left
              column at `lg+`. The fade-out mask on the right edge of the
              scroll container signals to mobile users that there are more
              tabs they can swipe to (the scrollbar itself is hidden for a
              cleaner look). The mask is disabled at `lg+` where the sidebar
              is vertical and the affordance isn't needed. */}
          <div className="bg-modal flex-shrink-0 w-full lg:w-[240px] lg:h-full overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto border-b border-component lg:border-b-0 lg:border-r [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black_calc(100%_-_36px),transparent)] [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_36px),transparent)] lg:[mask-image:none] lg:[-webkit-mask-image:none]">
            {/* Inner sidebar container: row < lg (horizontal tabs), col at lg+. */}
            <div className="flex flex-row lg:flex-col lg:justify-between overflow-hidden lg:h-full">
              {/* Tab sections wrapper. */}
              <div className="flex flex-row lg:flex-col lg:justify-between px-2 py-2 gap-0.5 lg:overflow-y-auto overflow-x-auto">
                {Object.entries(tabsBySection).map(([section, tabs]) => (
                  // Each section: row < lg (tabs flow inline next to the previous section), block at lg+ (with header above).
                  <div key={section} className="flex flex-row lg:block gap-0.5">
                    {/* Section header — hidden < lg so tabs flow as one continuous strip. */}
                    <div className="hidden lg:flex text-xs leading-none mb-0.5 mt-0 text-sec font-medium items-center h-7 px-2 text-ellipsis overflow-hidden">
                      {section}
                    </div>
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      return (
                        <div
                          key={tab.id}
                          role="tab"
                          tabIndex={isActive ? 0 : -1}
                          id={`settings-tab-${tab.id}`}
                          data-testid={`settings-tab-${tab.id}`}
                          aria-selected={isActive}
                          onClick={() => setActiveTab(tab.id)}
                          className={cn(
                            // `shrink-0` keeps each tab at its natural width
                            // when the sidebar flows horizontally on mobile —
                            // otherwise flex would compress them. On desktop
                            // (vertical layout) shrink-0 is a no-op.
                            "shrink-0 user-select-none transition-colors duration-200 ease-in cursor-pointer flex items-center justify-between px-2 py-0 rounded-md mt-0.5 mb-0 h-7 relative",
                            isActive
                              ? "bg-selected"
                              : "hover-component "
                          )}
                        >
                          <div className="flex items-center font-medium leading-none">
                            {tab.id === "profile" ? (
                              <div className="w-6 h-6 mr-2 text-zinc-500 dark:text-zinc-400 flex-shrink-0 flex items-center justify-center self-center leading-none">
                                <div className="flex items-center justify-center leading-none">
                                  <div className="bg-zinc-200 dark:bg-zinc-700 rounded-full shadow-sm outline outline-1 outline-offset-[-1px] outline-zinc-300 dark:outline-zinc-600">
                                    <div className="rounded-full w-[22px] h-[22px] flex items-center justify-center select-none opacity-100">
                                      <div className="w-full h-full">
                                        {user?.image ? (
                                          <img
                                            alt={user.name || "User"}
                                            src={user.image}
                                            referrerPolicy="same-origin"
                                            className="block object-cover rounded-full w-full h-full"
                                          />
                                        ) : (
                                          <div className="w-full h-full bg-zinc-300 dark:bg-zinc-600 rounded-full flex items-center justify-center text-xs font-medium">
                                            {user?.name?.charAt(0).toUpperCase() || "U"}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={cn(
                                  "w-6 h-6 mr-2 flex-shrink-0 flex items-center justify-center self-center leading-none",
                                  isActive
                                    ? "text-zinc-700 dark:text-zinc-300"
                                    : "text-zinc-500 dark:text-zinc-400"
                                )}
                              >
                                <div className="w-4 h-4 flex items-center justify-center overflow-hidden">
                                  <Icon className="w-full h-full block flex-shrink-0" />
                                </div>
                              </div>
                            )}
                            <div
                              className={cn(
                                "text-sm leading-none flex items-center",
                                isActive
                                  ? "text-zinc-900 dark:text-zinc-100"
                                  : tab.highlight
                                    ? "text-blue-500 dark:text-blue-500"
                                    : "text-zinc-600 dark:text-zinc-400"
                              )}
                            >
                              <div
                                className={cn(
                                  "text-sm leading-5 whitespace-nowrap overflow-hidden text-ellipsis font-medium",
                                  isActive
                                    ? "text-zinc-900 dark:text-zinc-100"
                                    : tab.highlight
                                      ? "text-blue-500 dark:text-blue-500"
                                      : "text-zinc-600 dark:text-zinc-400"
                                )}
                              >
                                {tab.id === "profile" ? (user?.name || tab.label) : tab.label}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div
            role="tabpanel"
            aria-labelledby={`settings-tab-${activeTab}`}
            id={`settings-tabpanel-${activeTab}`}
            className="flex-grow relative z-[1] h-full overflow-hidden"
          >
            <div className="flex flex-col w-full h-full bg-background">
              {/* Content padding — `px-[60px]` (120px total) was the original
                  desktop value; far too much on phones (would eat half the
                  viewport). Ladder it: 16px on phones, 24px on `sm`, 60px at
                  `lg+`. Same idea for vertical padding. */}
              <div className="z-[1] flex-grow px-4 sm:px-6 lg:px-[60px] pt-6 pb-6 lg:pt-9 lg:pb-9 overflow-auto clean-scrollbar">
                {renderTabContent()}
              </div>
              {/* Close Button */}
              <div className="absolute top-3 right-3 w-[22px] h-[22px] rounded-full bg-background dark:bg-zinc-900 z-[10]">
                <button
                  role="button"
                  tabIndex={0}
                  onClick={closeModal}
                  className="user-select-none transition-colors duration-200 ease-in cursor-pointer w-full h-full rounded-full flex justify-center items-center bg-transparent"
                >
                  <X className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function DeleteWorkspaceModalPortal() {
  const {
    showDeleteWorkspaceModal,
    setShowDeleteWorkspaceModal,
    isDeletingWorkspace,
    confirmDeleteWorkspace,
  } = useSettingsModal();
  const { currentWorkspace } = useWorkspaceContext();

  if (!showDeleteWorkspaceModal) return null;

  return createPortal(
    <DeleteConfirmationModal
      title="Delete Workspace"
      header="Delete Workspace"
      message={currentWorkspace?.name
        ? `Are you sure you want to delete the workspace ${currentWorkspace.name}? This action cannot be undone.`
        : "Are you sure you want to delete this workspace? This action cannot be undone."}
      isOpen={showDeleteWorkspaceModal}
      extraMessage="All workspace data, pages, and member information will be permanently deleted"
      isDeleting={isDeletingWorkspace}
      onCancel={() => setShowDeleteWorkspaceModal(false)}
      onConfirm={confirmDeleteWorkspace}
    />,
    document.body
  );
}

export default function SettingsModal() {
  return (
    <>
      <SettingsModalContent />
      <DeleteWorkspaceModalPortal />
    </>
  );
}

